import ShopifyOauth from '@/models/plugin/shopifyOauth';
import InstagramOauth from '@/models/plugin/instagramOauth';
import InstagramPluginModerator from '@/models/plugin/instagramPluginModerator';
import { formatDateTime } from '@/utils/format';

class Plugin {
  id: string;

  name: string;

  scope: string;

  redirectURI: string;

  type: 'shopify' | 'instagram' | 'facebook' | 'woocommerce' | 'magento' | 'bigcommerce' | 'custom';

  version: string;

  token: string;

  ownerUuid: string;

  adminsUuid: string[];

  createdAt: Date;

  updatedAt: Date;

  shopifyOauth: ShopifyOauth | null;

  instagramOauth: InstagramOauth | null;

  isOwner: boolean;

  profilePictureBase64: string;

  moderators: InstagramPluginModerator[];

  admins: string[];

  liveMedia: string;

  pendingFullSyncMediaId: string;

  lastSeenLive: Date | null;

  autoMessageEnabled: boolean;

  /** Connection health as the backend last observed it.
   *  '' / 'active' → working. 'token_refresh_failed' → the Instagram
   *  token expired and could not be renewed. Verified against production:
   *  only 1 of 457 plugins in that state streamed in the last 14 days,
   *  so it is a trustworthy signal, not stale noise. */
  status: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(data: any) {
    // eslint-disable-next-line no-underscore-dangle
    this.id = data.id || data._id || data.Id;
    this.name = data.name;
    this.scope = data.scope;
    this.type = data.type;
    this.redirectURI = data.redirectURI;
    this.version = data.version;
    this.token = data.token;
    this.ownerUuid = data.ownerUuid;
    this.adminsUuid = data.adminsUuid;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.shopifyOauth = data.shopifyOauth;
    this.instagramOauth = data.instagramOauth;
    this.isOwner = data.isOwner;
    this.profilePictureBase64 = data.profilePictureBase64;
    this.moderators = data.moderators;
    this.admins = data.admins;
    // Livvia post-live signal + settings. Support both snake_case (raw Go
    // response) and camelCase (already-transformed) for safety.
    this.liveMedia = data.liveMedia || data.live_media || '';
    this.pendingFullSyncMediaId = data.pendingFullSyncMediaId || data.pending_full_sync_media_id || '';
    const lastSeenRaw = data.lastSeenLive || data.last_seen_live;
    this.lastSeenLive = lastSeenRaw ? new Date(lastSeenRaw) : null;
    this.status = data.status || '';
    const settings = data.settings || {};
    const autoFlag = settings.autoMessageEnabled ?? settings.auto_message_enabled;
    // Default true — matches pre-toggle behaviour where every plugin had automation on.
    this.autoMessageEnabled = autoFlag === undefined ? true : Boolean(autoFlag);
  }

  /** True when the Instagram connection needs the seller to re-authorise.
   *  Comments still arrive over the webhook while this is true — what
   *  stops is every token-backed feature: automated replies (0 dispatches
   *  across 457 affected plugins in 21 days), live detection and sync.
   *  That asymmetry is why the seller never notices on her own. */
  needsReconnect(): boolean {
    return this.type === 'instagram' && this.status === 'token_refresh_failed';
  }

  isOldVersion(): boolean {
    return !this.version;
  }

  isShopify(): boolean {
    return this.type === 'shopify';
  }

  isInstagram(): boolean {
    return this.type === 'instagram';
  }

  getDescription(): string {
    if (this.isShopify()) {
      return 'Você poderá escolher seus produtos já cadastrados no Shopify para usar na sua lojinha da bio ou em suas Lives.';
    }
    if (this.isInstagram()) {
      return 'Conectar sua conta do Instagram para usar em suas Lives e nos ajudar a verificar sua identidade.';
    }
    return '';
  }

  // Locale-aware date+time following the selected app language.
  formattedCreatedAt(): string {
    return formatDateTime(this.createdAt, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  formattedScopes(): string {
    const scopes = this.scope.split(',');
    return scopes.map((scope) => Plugin.formatScope(scope)).join(', ');
  }

  static formatScope(scope: string): string {
    switch (scope) {
      case 'read_products':
        return 'Ler produtos';
      case 'business_basic': // Instagram
        return 'Acesso básico a informações';
      case 'business_manage_messages': // Instagram
        return 'Gerenciar mensagens';
      case 'business_manage_comments': // Instagram
        return 'Gerenciar comentários';
      case 'business_content_publish': // Instagram
        return 'Publicar conteúdo';

      case 'instagram_business_basic': // Instagram
        return 'Dados básicos';
      case 'instagram_business_manage_comments': // Instagram
        return 'Gerenciar comentários';
      case 'instagram_business_manage_messages': // Instagram
        return 'Responder comentários';
      default:
        return '-';
    }
  }

  static newPlugin(params: {
    name: string,
    type: string,
    redirectURI: string,
    shopifyOauth: ShopifyOauth | null,
    moderators: InstagramPluginModerator[],
  }): Plugin {
    return new Plugin({
      id: '',
      name: params.name,
      type: params.type,
      redirectURI: params.redirectURI,
      version: '',
      token: '',
      ownerUuid: '',
      adminsUuid: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      moderators: params.moderators ? params.moderators.map(
        (moderator: any) => new InstagramPluginModerator(moderator),
      ) : [],
      shopifyOauth: params.shopifyOauth ? new ShopifyOauth({
        code: params.shopifyOauth.code,
        hmac: params.shopifyOauth.hmac,
        host: params.shopifyOauth.host,
        shop: params.shopifyOauth.shop,
        state: params.shopifyOauth.state,
        timestamp: params.shopifyOauth.timestamp,
      }) : null,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromApiResponse(columns: string[], rows: any[][]): Plugin[] {
    if (rows.length === 0) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return rows.map((row: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const v: any = {};
      columns.forEach((column, index) => {
        const camelCaseKey = column.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
        if (row[index] && (camelCaseKey === 'updatedAt' || camelCaseKey === 'createdAt')) {
          v[camelCaseKey] = new Date(row[index]);
        } else if (row[index] && (camelCaseKey === 'shopifyOauth') && row[index].type === 'shopify') {
          v[camelCaseKey] = new ShopifyOauth(row[index]);
        } else if (row[index] && (camelCaseKey === 'instagramOauth') && row[index].type === 'instagram') {
          v[camelCaseKey] = new InstagramOauth(row[index]);
        } else {
          v[camelCaseKey] = row[index];
        }
      });
      return new Plugin(v);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromObject(data: any): Plugin {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v: any = {};

    Object.keys(data).forEach((key) => {
      const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      if (data[key] && (camelCaseKey === 'updatedAt' || camelCaseKey === 'createdAt')) {
        v[camelCaseKey] = new Date(data[key]);
      } else if (data[key] && camelCaseKey === 'shopifyOauth') {
        v[camelCaseKey] = new ShopifyOauth(data[key]);
      } else if (data[key] && camelCaseKey === 'instagramOauth') {
        v[camelCaseKey] = new InstagramOauth(data[key]);
      } else {
        v[camelCaseKey] = data[key];
      }
    });

    return new Plugin(v);
  }
}

export default Plugin;
